export type KeywordMappingImportStatus = 'uploading' | 'queued' | 'running' | 'done' | 'failed'

export interface KeywordMappingImport {
    keyword_mapping_import_id: number
    status: KeywordMappingImportStatus
    file_name: string
    file_size: number
    total_rows: number
    processed_rows: number
    added_rows: number
    updated_rows: number
    unchanged_rows: number
    failed_rows: number
    duplicate_rows: number
    error_message: string | null
    createdByName: string | null
    created: string
    modified: string
    finished: string | null
    stalled: boolean
    reportAvailable: boolean
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null
}

function readString(value: unknown): string {
    if (typeof value === 'string') return value
    return ''
}

function readNullableString(value: unknown): string | null {
    if (typeof value === 'string') return value
    return null
}

function readStatus(value: unknown): KeywordMappingImportStatus | null {
    if (value === 'uploading') return value
    if (value === 'queued') return value
    if (value === 'running') return value
    if (value === 'done') return value
    if (value === 'failed') return value
    return null
}

function readCount(value: unknown): number | null {
    if (typeof value !== 'number') return null
    if (Number.isInteger(value) === false) return null
    if (value < 0) return null
    return value
}

function readBoolean(value: unknown): boolean | null {
    if (value === true) return true
    if (value === false) return false
    return null
}

export function readKeywordMappingImport(value: unknown): KeywordMappingImport | null {
    if (!isRecord(value)) return null
    if (typeof value.keyword_mapping_import_id !== 'number') return null
    if (Number.isInteger(value.keyword_mapping_import_id) === false) return null

    const status = readStatus(value.status)
    if (status === null) return null
    if (typeof value.file_name !== 'string') return null

    const fileSize = readCount(value.file_size)
    const totalRows = readCount(value.total_rows)
    const processedRows = readCount(value.processed_rows)
    const addedRows = readCount(value.added_rows)
    const updatedRows = readCount(value.updated_rows)
    const unchangedRows = readCount(value.unchanged_rows)
    const failedRows = readCount(value.failed_rows)
    const duplicateRows = readCount(value.duplicate_rows)
    if (fileSize === null) return null
    if (totalRows === null) return null
    if (processedRows === null) return null
    if (addedRows === null) return null
    if (updatedRows === null) return null
    if (unchangedRows === null) return null
    if (failedRows === null) return null
    if (duplicateRows === null) return null

    const stalled = readBoolean(value.stalled)
    const reportAvailable = readBoolean(value.reportAvailable)
    if (stalled === null) return null
    if (reportAvailable === null) return null

    return {
        keyword_mapping_import_id: value.keyword_mapping_import_id,
        status,
        file_name: value.file_name,
        file_size: fileSize,
        total_rows: totalRows,
        processed_rows: processedRows,
        added_rows: addedRows,
        updated_rows: updatedRows,
        unchanged_rows: unchangedRows,
        failed_rows: failedRows,
        duplicate_rows: duplicateRows,
        error_message: readNullableString(value.error_message),
        createdByName: readNullableString(value.createdByName),
        created: readString(value.created),
        modified: readString(value.modified),
        finished: readNullableString(value.finished),
        stalled,
        reportAvailable,
    }
}

export function readKeywordMappingImports(value: unknown): KeywordMappingImport[] {
    if (!Array.isArray(value)) return []

    const items: KeywordMappingImport[] = []
    for (const item of value) {
        const parsed = readKeywordMappingImport(item)
        if (parsed === null) continue
        items.push(parsed)
    }

    return items
}
